// src/services/searchAll.js — search everything, honestly.
//
// ── WHAT IT SEARCHES ────────────────────────────────────────────────────────
// The phone's exported history (already decrypted per chat) plus each chat's
// live decrypted preview. Every message kind is classified and made findable:
// text, photos, videos, albums, voice notes, documents, links, polls,
// locations, contacts, flight cards, shared Vibes posts, group invitations,
// stickers/GIFs, calls and system rows.
//
// ── WHAT IT REFUSES TO SHOW (the important half) ─────────────────────────────
// A search box that resurrects deleted messages is worse than no search box.
// Four separate deletions are honoured, all of which sync from the app:
//   1. deleted for everyone       m.isDeleted / m.deleted
//   2. deleted for me             m.deletedFor (array) contains my uid
//   3. cleared chat               anything at or before chats/{id}.clearedAt[me]
//   4. media purged from storage  m.mediaDeletedFromStorage — the bytes are gone
// plus the machinery rows that are never messages at all (delete_command,
// reaction_action, vote_action) and chats already hidden by chats/{id}.deletedFor.

import { clearedAtMs, isDeletedFor, tsMs } from './chats';
import { isMediaCleared } from './chatClear';

/* ── the kinds, in the order the results are grouped ───────────────────────── */
export const KINDS = [
  { id: 'messages', name: 'Messages' },
  { id: 'media', name: 'Photos & videos' },
  { id: 'links', name: 'Links' },
  { id: 'docs', name: 'Documents' },
  { id: 'voice', name: 'Voice' },
  { id: 'polls', name: 'Polls' },
  { id: 'places', name: 'Locations' },
  { id: 'contacts', name: 'Contacts' },
  { id: 'flights', name: 'Flights' },
  { id: 'posts', name: 'Shared posts' },
  { id: 'invites', name: 'Invitations' },
  { id: 'calls', name: 'Calls' },
  { id: 'system', name: 'Updates' },
];

const NEVER_A_ROW = new Set(['delete_command', 'reaction_action', 'vote_action']);

/** Which bucket a message belongs to, and the text worth matching on. */
export function classify(m) {
  const t = String(m?.type || '');
  const st = String(m?.systemType || '');

  if (st === 'group_invite') {
    return { kind: 'invites', label: 'Group invitation', body: `${m.groupName || ''} ${m.senderName || ''}`.trim() };
  }
  if (st === 'call_history' || t === 'missed_call') {
    const cd = m.callData || {};
    const kindTxt = cd.callType === 'video' ? 'Video call' : 'Voice call';
    return { kind: 'calls', label: kindTxt, body: `${kindTxt} ${cd.outcome || ''}`.trim() };
  }
  if (t === 'system' || st) {
    return { kind: 'system', label: 'Update', body: String(m.text || '') };
  }

  switch (t) {
    case 'image':
      return { kind: 'media', label: 'Photo', body: m._caption || m.caption || 'Photo' };
    case 'video':
      return { kind: 'media', label: 'Video', body: m._caption || m.caption || 'Video' };
    case 'images':
      return {
        kind: 'media',
        label: `Album · ${(m.images || m._mediaBatch || []).length || ''}`.trim(),
        body: m._caption || m.caption || 'Album',
      };
    case 'audio':
      return { kind: 'voice', label: 'Voice message', body: m._caption || 'Voice message' };
    case 'document':
      return { kind: 'docs', label: m.documentName || 'Document', body: m.documentName || 'Document' };
    case 'poll':
      return {
        kind: 'polls',
        label: 'Poll',
        body: `${m.question || m.pollQuestion || ''} ${(m.options || []).map((o) => o?.text || o).join(' ')}`.trim(),
      };
    case 'location':
      return { kind: 'places', label: 'Location', body: m.locationName || m.address || m.placeName || 'Location' };
    case 'contact':
      return { kind: 'contacts', label: 'Contact', body: `${m.contactName || ''} ${m.contactPhone || ''}`.trim() || 'Contact' };
    case 'flight':
      return {
        kind: 'flights',
        label: 'Flight',
        body: `${m.flightNumber || ''} ${m.from || ''} ${m.to || ''} ${m.airline || ''}`.trim() || 'Flight',
      };
    case 'shared_post':
      return {
        kind: 'posts',
        label: 'Shared post',
        body: `${m.postOwnerName || ''} ${m.postContent || ''}`.trim() || 'Shared post',
      };
    case 'sticker': case 'gif':
      return { kind: 'media', label: t === 'gif' ? 'GIF' : 'Sticker', body: t === 'gif' ? 'GIF' : 'Sticker' };
    default: {
      const plain = m._plain ?? m.text ?? '';
      // a text message carrying a preview card is ALSO a link
      if (m.linkMetadata) {
        let meta = m.linkMetadata;
        if (typeof meta === 'string') { try { meta = JSON.parse(meta); } catch { meta = null; } }
        return {
          kind: 'links',
          label: meta?.siteName || meta?.title || 'Link',
          body: `${plain} ${meta?.title || ''} ${meta?.url || ''} ${meta?.description || ''}`.trim(),
        };
      }
      return { kind: 'messages', label: 'Message', body: String(plain || '') };
    }
  }
}

/** Is this message allowed to appear in results at all? */
export function isSearchable(m, chat, myUid) {
  if (!m) return false;
  if (NEVER_A_ROW.has(String(m.systemType || '')) || NEVER_A_ROW.has(String(m.type || ''))) return false;
  if (m.isDeleted || m.deleted) return false;                 // deleted for everyone
  if (isDeletedFor(m, myUid)) return false;                    // deleted for me
  if (m.mediaDeletedFromStorage) return false;                 // the bytes are gone
  if (isMediaCleared(m, chat, myUid)) return false;            // media cleared by me
  const cleared = clearedAtMs(chat, myUid);
  if (cleared && tsMs(m.timestamp) <= cleared) return false;   // cleared chat
  return true;
}

const norm = (s) => String(s || '').toLowerCase();

/**
 * Run the search.
 * @param term      what was typed
 * @param chats     the visible chat list (already excludes chats deleted for me)
 * @param history   syncedHistory — { chatId: [messages] } from the phone
 * @param previews  { chatId: {text, ts, senderId} } live decrypted previews
 * @param myUid
 * @returns { total, byKind: { kind: [hit] } } — hits newest first
 */
export function searchAll({ term, chats, history, previews, myUid }) {
  const q = norm(term).trim();
  const byKind = {};
  if (!q) return { total: 0, byKind };

  let total = 0;
  const push = (hit) => {
    (byKind[hit.kind] = byKind[hit.kind] || []).push(hit);
    total += 1;
  };

  const seen = new Set();

  for (const chat of chats || []) {
    const rows = [
      ...(history?.[chat.id] || []),
      // the live preview may be newer than the last export
      ...(previews?.[chat.id]?.text
        ? [{ id: `pv-${chat.id}`, type: 'text', _plain: previews[chat.id].text, timestamp: previews[chat.id].ts, senderId: previews[chat.id].senderId }]
        : []),
    ];

    for (const m of rows) {
      if (!isSearchable(m, chat, myUid)) continue;
      const key = `${chat.id}|${m.id}`;
      if (seen.has(key)) continue;

      const { kind, label, body } = classify(m);
      // a kind's own label is searchable too — typing "poll" finds polls
      if (!norm(body).includes(q) && !norm(label).includes(q)) continue;

      seen.add(key);
      push({
        kind,
        label,
        chat,
        chatId: chat.id,
        messageId: String(m.id || ''),
        senderId: m.senderId,
        mine: m.senderId === myUid,
        ts: tsMs(m.timestamp),
        body: String(body || ''),
        thumb: m.imageThumbnailBase64 || m.videoThumbnailBase64 || (m.batchThumbnails || [])[0] || null,
      });
    }
  }

  for (const k of Object.keys(byKind)) byKind[k].sort((a, b) => b.ts - a.ts);
  return { total, byKind };
}

/** "Today" / "Yesterday" / "March 2025" — the heading a hit sits under. */
export function dateBucket(ms) {
  if (!ms) return 'Earlier';
  const d = new Date(ms);
  const now = new Date();
  const sameDay = (a, b) => a.toDateString() === b.toDateString();
  if (sameDay(d, now)) return 'Today';
  const y = new Date(now); y.setDate(y.getDate() - 1);
  if (sameDay(d, y)) return 'Yesterday';
  const week = new Date(now); week.setDate(week.getDate() - 6);
  if (d >= week) return d.toLocaleDateString(undefined, { weekday: 'long' });
  if (d.getFullYear() === now.getFullYear()) return d.toLocaleDateString(undefined, { month: 'long' });
  return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}
